import type { Ring, Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { prismSolid } from '../../solids';
import type { FortressStyles } from './fortressParts';
import { panel } from './fortressBlocks';
import {
  CITY_GABLE,
  CITY_WINDOWS,
  CULVERT_MOUTH,
  CULVERT_WALLS,
  FRIEZES,
  HOOFDPOORT,
  rectRing,
} from './sintAntoniespoortPlan';

/* Friezes and bands reach this far into the wall they stand on, so no seam opens along it. */
const EMBED = 0.05;

interface Line {
  readonly from: Vec2;
  readonly to: Vec2;
}

interface FriezeHeights {
  readonly corbels: number;
  readonly band: number;
  readonly top: number;
  readonly reach: number;
}

/* A strip beside a wall line, `d0` to `d1` out from it (the wall stands to the line's left). */
function strip(line: Line, s: readonly [number, number], d: readonly [number, number]): Ring {
  const len = Math.hypot(line.to[0] - line.from[0], line.to[1] - line.from[1]);
  const t: Vec2 = [(line.to[0] - line.from[0]) / len, (line.to[1] - line.from[1]) / len];
  const out: Vec2 = [t[1], -t[0]];
  const at = (a: number, b: number): Vec2 => [
    line.from[0] + t[0] * a + out[0] * b,
    line.from[1] + t[1] * a + out[1] * b,
  ];
  return [at(s[0], d[0]), at(s[1], d[0]), at(s[1], d[1]), at(s[0], d[1])];
}

/** An arched corbel frieze along a wall face: a band on corbels at `spacing`, as many as fit. */
function frieze(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  line: Line,
  spec: FriezeHeights
): void {
  const len = Math.hypot(line.to[0] - line.from[0], line.to[1] - line.from[1]);
  const closed = { top: true, bottom: true };
  const band = strip(line, [0, len], [-EMBED, spec.reach]);
  prismSolid(builder, style, frame, band, spec.band, spec.top, closed);
  const count = Math.floor(len / FRIEZES.spacing);
  const bay = len / count;
  const half = FRIEZES.corbelWidth / 2;
  for (let k = 0; k < count; k++) {
    const mid = bay * (k + 0.5);
    const corbel = strip(line, [mid - half, mid + half], [-EMBED, spec.reach * 0.8]);
    prismSolid(builder, style, frame, corbel, spec.corbels, spec.band, closed);
  }
}

/** The friezes of the hoofdpoort's flanks and field front and of the walls over the culvert. */
export function friezes(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const [u, v] = [HOOFDPOORT.u, HOOFDPOORT.v];
  const hoofdpoort: Line[] = [
    { from: [u[0], v[0]], to: [u[1], v[0]] },
    { from: [u[1], v[0]], to: [u[1], v[1]] },
    { from: [u[1], v[1]], to: [u[0], v[1]] },
  ];
  for (const line of hoofdpoort) frieze(builder, styles.wall, frame, line, FRIEZES.hoofdpoort);
  const cu = CULVERT_WALLS.u;
  const [west, east] = [CULVERT_WALLS.v[0][0], CULVERT_WALLS.v[1][1]];
  const culvert: Line[] = [
    { from: [cu[0], west], to: [cu[1], west] },
    { from: [cu[1], east], to: [cu[0], east] },
  ];
  for (const line of culvert) frieze(builder, styles.wall, frame, line, FRIEZES.culvert);
}

/** The dark of the culvert's mouths, and of the city front's windows. */
export function openingsOfTheFronts(
  builder: MeshBuilder,
  styles: FortressStyles,
  frame: Frame
): void {
  const { u, sill, head } = CULVERT_MOUTH;
  const mid = (u[0] + u[1]) / 2;
  const mouth = { width: u[1] - u[0], sill, head, arch: false, proud: 0.03, depth: 0.2 };
  const [west, east] = [CULVERT_WALLS.v[0][0], CULVERT_WALLS.v[1][1]];
  panel(builder, styles.dark, frame, { ...mouth, at: [mid, west], facing: -Math.PI / 2 });
  panel(builder, styles.dark, frame, { ...mouth, at: [mid, east], facing: Math.PI / 2 });
  for (const w of CITY_WINDOWS) {
    panel(builder, styles.dark, frame, {
      ...w,
      at: [HOOFDPOORT.u[0], w.v],
      facing: Math.PI,
      arch: true,
      proud: 0.05,
      depth: 0.2,
    });
  }
}

/**
 * The stepped gable over the hoofdpoort's city front: a parapet as wide as the wall at the eaves,
 * each step standing its coping above the roof line at its inner edge, and a crown over the ridge.
 */
export function steppedGable(builder: MeshBuilder, style: VertexStyle, frame: Frame): void {
  const { u, v, walk, roof } = HOOFDPOORT;
  const { thickness, steps, coping, crown } = CITY_GABLE;
  const half = (roof.v[1] - roof.v[0]) / 2;
  const slope = (roof.ridge - walk) / half;
  const width = half / (steps + 0.5);
  const slab = [u[0], u[0] + thickness] as const;
  const closed = { top: true, bottom: true };
  let bottom: number = walk;
  for (let k = 0; k < steps; k++) {
    const span = k === 0 ? v : ([roof.v[0] + k * width, roof.v[1] - k * width] as const);
    const top = walk + slope * (k + 1) * width + coping;
    prismSolid(builder, style, frame, rectRing(slab, span), bottom, top, closed);
    bottom = top;
  }
  const middle = [roof.v[0] + steps * width, roof.v[1] - steps * width] as const;
  prismSolid(builder, style, frame, rectRing(slab, middle), bottom, roof.ridge + crown, closed);
}

/** The roof starts inside the gable, which hides its end. */
export const ROOF_START = HOOFDPOORT.u[0] + CITY_GABLE.thickness - 0.1;
